/**
 * Detector de actividad humana
 * Monitorea eventos del usuario y actualiza el timestamp global
 */

import { idleConfig } from './config';
import { getBus } from './bus';
import { ACTIVITY_EVENTS, MESSAGE_TYPES } from './constants';

class ActivityDetector {
  private isActive = false;
  private lastThrottledTime = 0;
  private throttleTimeoutId: number | null = null;
  private lastActivityLogTime = 0;

  start() {
    if (this.isActive) {
      return;
    }

    this.isActive = true;
    this.setupEventListeners();

    // Registrar actividad inicial
    this.recordActivity();
  }

  stop() {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;
    this.removeEventListeners();

    if (this.throttleTimeoutId) {
      clearTimeout(this.throttleTimeoutId);
      this.throttleTimeoutId = null;
    }
  }

  private setupEventListeners() {
    // Eventos inmediatos (sin throttle)
    ACTIVITY_EVENTS.IMMEDIATE.forEach(eventType => {
      document.addEventListener(eventType, this.handleImmediateActivity, {
        passive: true,
        capture: true
      });
    });

    // Eventos con throttle
    ACTIVITY_EVENTS.THROTTLED.forEach(eventType => {
      document.addEventListener(eventType, this.handleThrottledActivity, {
        passive: true,
        capture: true
      });
    });
  }

  private removeEventListeners() {
    // Remover eventos inmediatos
    ACTIVITY_EVENTS.IMMEDIATE.forEach(eventType => {
      document.removeEventListener(eventType, this.handleImmediateActivity, { 
        capture: true 
      });
    });

    // Remover eventos con throttle
    ACTIVITY_EVENTS.THROTTLED.forEach(eventType => {
      document.removeEventListener(eventType, this.handleThrottledActivity, { 
        capture: true 
      });
    });
  }

  private handleImmediateActivity = () => {
    if (!this.isActive) {
      return;
    }

    const now = Date.now();
    const timeSinceLastLog = now - (this.lastActivityLogTime || 0);

    if (timeSinceLastLog > 2000) {
      this.lastActivityLogTime = now;
    }

    this.recordActivity();
  };

  private handleThrottledActivity = () => {
    if (!this.isActive) return;

    const now = Date.now();
    const timeSinceLastThrottled = now - this.lastThrottledTime;

    // Si ya hay un timeout pendiente, no hacer nada
    if (this.throttleTimeoutId) {
      return;
    }

    // Si ha pasado suficiente tiempo, registrar inmediatamente
    if (timeSinceLastThrottled >= idleConfig.activityThrottleMs) {
      this.recordActivity();
      this.lastThrottledTime = now;
      return;
    }

    // Programar para el futuro
    const delay = idleConfig.activityThrottleMs - timeSinceLastThrottled;

    this.throttleTimeoutId = window.setTimeout(() => {
      this.throttleTimeoutId = null;
      if (this.isActive) {
        this.recordActivity();
        this.lastThrottledTime = Date.now();
      }
    }, delay);
  };

  private recordActivity() {
    const now = Date.now();

    // Actualizar localStorage con last-write-wins
    this.updateLastActivityStorage(now);

    // Notificar a otras pestañas
    this.notifyActivity(now);
  }

  private updateLastActivityStorage(timestamp: number) {
    try {
      const currentValue = localStorage.getItem(idleConfig.storageKeys.lastActivity);
      const currentTimestamp = currentValue ? parseInt(currentValue, 10) : 0;

      // Solo actualizar si el nuevo timestamp es mayor (last-write-wins)
      if (timestamp > currentTimestamp) {
        localStorage.setItem(idleConfig.storageKeys.lastActivity, timestamp.toString());   
      } else {
        throw new Error('Timestamp not newer');
      }
    } catch (error) {
      console.error('Error updating localStorage activity:', error);
    }
  }

  private notifyActivity(timestamp: number) {
    try {
      const bus = getBus();
      const message = {
        type: MESSAGE_TYPES.ACTIVITY,
        ts: timestamp,
      };

      bus.publish(message);
    } catch (error) {
      console.error('Error notifying activity to other tabs:', error);
    }
  }

  // Método para obtener la última actividad registrada
  getLastActivity(): number {
    try {
      const value = localStorage.getItem(idleConfig.storageKeys.lastActivity);
      const timestamp = value ? parseInt(value, 10) : Date.now();

      return timestamp;
    } catch (error) {
      console.error('Error reading last activity from localStorage:', error);
      return Date.now();
    }
  }
}

// Singleton instance
let detectorInstance: ActivityDetector | null = null;

export const getActivityDetector = (): ActivityDetector => {
  if (!detectorInstance) {
    detectorInstance = new ActivityDetector();
  }
  return detectorInstance;
};

export const destroyActivityDetector = () => {
  if (detectorInstance) {
    detectorInstance.stop();
    detectorInstance = null;
  }
};
